import React from "react";
import { Button } from "antd";
import "../components/styles/messagebox.css";
import { reportError, toErrorString } from "../utils/errorLogger.js";

/**
 * Last line of defence for render-time throws.
 *
 * React unmounts the WHOLE tree when a render/lifecycle throw reaches the root, so without a boundary any
 * single bad record (a malformed calendar invite, an unexpected header shape) blanks the entire app with no
 * message and no way back — the user just sees white. This catches that, reports it, and offers a way out.
 *
 * Must stay a class component: there is no hook equivalent of componentDidCatch.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Unhandled render error:", error, errorInfo);
    try {
      const errorStr = toErrorString(error || error?.message);
      reportError({
        endpoint: "-",
        message: `FileName: ErrorBoundary.js, Fun: componentDidCatch, ${String(error?.message || "Unhandled render error")}`,
        error: errorStr,
        userName: localStorage.getItem("UserName") || "",
      });
    } catch (reportErr) {
      // Reporting must never itself take down the fallback UI.
      console.error(reportErr);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoToInbox = () => {
    // Full navigation, not a router push: the router lives inside this boundary and the crashed tree is
    // still mounted, so a soft navigation would re-render straight back into the throw.
    window.location.href = "/app/inbox";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="noInternetdiv" style={{ minHeight: "100vh", padding: "20px" }}>
          <h1 className="title">Something went wrong</h1>
          <p className="message">
            dMail hit an unexpected error while displaying this page. Your mail is safe — reloading usually
            fixes it.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button onClick={this.handleReload} className="p-0">
              <span className="text fs-15px fw-bold text-white border-0 p-5px rounded-5px">Reload</span>
            </Button>
            <Button onClick={this.handleGoToInbox} className="p-0">
              <span className="text fs-15px fw-bold text-white border-0 p-5px rounded-5px">Go to Inbox</span>
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
